/**
 * CareLogix™ ORACLE — LifeCommand™ (Phase 36)
 *
 * User-facing personal health intelligence console. Decision-support only.
 * No diagnosis. No treatment. Reuses ORACLE dark green/gold visual system.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getAdminToken } from "@/api/adminOneClient";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

const COMPLIANCE = (
  "Decision-support only. Not medical advice. Not a diagnosis. Not a " +
  "treatment plan. Human review and clinician consultation required for " +
  "any escalation."
);

const BAND_COLOR = {
  "Elite":           "#7AC860",
  "Excellent":       "#7AC860",
  "Good":            "#c5a059",
  "Moderate":        "#fcd34d",
  "Needs Attention": "#ef4444",
};

const RISK_COLOR = {
  HIGH:     "#ef4444",
  MODERATE: "#fcd34d",
  LOW:      "#60B8E8",
  MINIMAL:  "#7AC860",
  NO_DATA_AVAILABLE: "#94a3b8",
};

async function _get(p) {
  try {
    const r = await fetch(`${BACKEND}${p}`);
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

async function _post(p, body) {
  try {
    const r = await fetch(`${BACKEND}${p}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return await r.json();
  } catch (e) { return { ok: false, error: String(e) }; }
}

const S = {
  page: { minHeight: "100vh", padding: "32px 40px 80px", background: "linear-gradient(160deg, #050b09 0%, #06231b 60%, #050b09 100%)", color: "#dfe3eb", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 28, fontFamily: "'Cinzel', serif", letterSpacing: "0.02em" },
  sub: { color: "#cbd5e1", marginTop: 8, fontSize: 13, maxWidth: 760, lineHeight: 1.5 },
  link: { color: "#c5a059", textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.1em", marginRight: 18 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginTop: 14, marginBottom: 24 },
  kpi: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 12, padding: "14px 16px" },
  kpiLabel: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.14em" },
  kpiValue: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 30, marginTop: 6 },
  kpiSub: { color: "#94a3b8", fontSize: 11, marginTop: 4 },
  section: { background: "rgba(13,26,15,0.65)", border: "1px solid rgba(245,200,66,0.20)", borderRadius: 14, padding: 18, marginBottom: 20 },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, margin: 0, marginBottom: 4 },
  sectionSub: { color: "#cbd5e1", fontSize: 12, lineHeight: 1.5, marginBottom: 12, marginTop: 4 },
  inputRow: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 },
  inputBox: { display: "flex", flexDirection: "column", minWidth: 150, flex: 1 },
  inputLabel: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em", marginBottom: 4 },
  input: { background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "9px 11px", borderRadius: 10, fontSize: 12.5 },
  btn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "9px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  btnSecondary: { appearance: "none", background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#cbd5e1", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 10px", border: "1px solid", borderRadius: 999 },
  list: { margin: "8px 0 0 18px", padding: 0, color: "#cbd5e1", fontSize: 13, lineHeight: 1.7 },
  quickActions: { display: "flex", flexWrap: "wrap", gap: 8 },
  quickBtn: { padding: "7px 12px", border: "1px solid rgba(245,200,66,0.35)", borderRadius: 999, background: "rgba(245,200,66,0.08)", color: "#fde68a", cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em" },
  compliance: { marginTop: 28, padding: "14px 18px", background: "rgba(252,211,77,0.06)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 12, color: "#fde68a", fontSize: 12, lineHeight: 1.5 },
  noData: { color: "#fcd34d", fontFamily: "'Space Mono', monospace", fontSize: 11 },
};

function Pill({ children, color }) {
  return <span style={{ ...S.pill, color, borderColor: color, background: `${color}1a` }}>{children}</span>;
}

function KPI({ label, value, sub, testid }) {
  return (
    <div style={S.kpi} data-testid={testid}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={S.kpiValue}>{value}</div>
      {sub ? <div style={S.kpiSub}>{sub}</div> : null}
    </div>
  );
}

export default function OracleLifeCommand() {
  const [userId, setUserId] = useState("");
  const [score, setScore]   = useState(null);
  const [history, setHistory] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [animations, setAnimations]     = useState([]);
  const [wearables, setWearables]       = useState([]);
  const [conditions, setConditions]     = useState([]);
  const [busy, setBusy]                 = useState("");
  const [recoveryBrief, setRecoveryBrief] = useState(null);
  const [flareBrief, setFlareBrief]       = useState(null);
  const [activeAnim, setActiveAnim]       = useState(null);

  // metric form
  const [form, setForm] = useState({
    sleep_hours: "", activity_minutes: "", hydration_oz: "",
    medication_adherence_pct: "", recovery_index: "", symptom_score: "",
    hrv: "", resting_heart_rate: "", pain_score: "",
  });

  const _loadedRef = useRef(false);

  const loadCatalogs = useCallback(async () => {
    const [qa, anim, wr, cond] = await Promise.all([
      _get("/api/oracle/lifecommand/quick-actions"),
      _get("/api/oracle/lifecommand/exercises/animations"),
      _get("/api/oracle/lifecommand/wearables/catalog"),
      _get("/api/oracle/lifecommand/autoimmune/conditions"),
    ]);
    setQuickActions((qa && qa.actions) || []);
    setAnimations((anim && anim.animations) || []);
    setWearables((wr && wr.providers) || []);
    setConditions((cond && cond.conditions) || []);
  }, []);

  useEffect(() => {
    if (_loadedRef.current) return;
    _loadedRef.current = true;
    const t = setTimeout(() => {
      loadCatalogs();
      try {
        const saved = window.localStorage.getItem("carelogix_lifecommand_user");
        if (saved) setUserId(saved);
      } catch (_e) { /* ignore */ }
    }, 0);
    return () => clearTimeout(t);
  }, [loadCatalogs]);

  const loadUser = useCallback(async (uid) => {
    if (!uid) return;
    const [s, h] = await Promise.all([
      _get(`/api/oracle/lifecommand/score/${encodeURIComponent(uid)}`),
      _get(`/api/oracle/lifecommand/history/${encodeURIComponent(uid)}?limit=14`),
    ]);
    setScore(s || null);
    setHistory((h && h.history) || []);
  }, []);

  const onSelectUser = useCallback(() => {
    if (!userId || userId.length < 2) return;
    try { window.localStorage.setItem("carelogix_lifecommand_user", userId); } catch (_e) { /* ignore */ }
    loadUser(userId);
  }, [userId, loadUser]);

  const submitMetrics = useCallback(async () => {
    if (!userId || userId.length < 2) return;
    setBusy("submit");
    const payload = { user_id: userId, source: "manual" };
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) payload[k] = Number(v);
    });
    const out = await _post("/api/oracle/lifecommand/metrics", payload);
    setBusy("");
    if (out && out.ok) {
      setForm({ sleep_hours: "", activity_minutes: "", hydration_oz: "", medication_adherence_pct: "", recovery_index: "", symptom_score: "", hrv: "", resting_heart_rate: "", pain_score: "" });
      await loadUser(userId);
    }
  }, [userId, form, loadUser]);

  const runRecovery = useCallback(async () => {
    if (!userId) return;
    setBusy("recovery");
    const out = await _post("/api/oracle/lifecommand/recovery-check", { user_id: userId });
    setBusy("");
    if (out && out.ok) setRecoveryBrief(out.brief);
  }, [userId]);

  const runFlare = useCallback(async (condition) => {
    if (!userId) return;
    setBusy("flare");
    const out = await _post("/api/oracle/lifecommand/flare-check", { user_id: userId, condition });
    setBusy("");
    if (out && out.ok) setFlareBrief(out.brief);
  }, [userId]);

  const lifescoreValue = score && score.lifescore != null ? score.lifescore : null;
  const lifescoreBand  = score && score.band ? score.band : "—";
  const lifescoreColor = BAND_COLOR[lifescoreBand] || "#94a3b8";

  const liveWearables = useMemo(() => wearables.filter((w) => w.status === "LIVE"), [wearables]);

  return (
    <main style={S.page} data-testid="oracle-lifecommand-page">
      <header style={S.header}>
        <div>
          <div style={S.eyebrow}>PHASE 36 · ORACLE LIFECOMMAND™</div>
          <h1 style={S.title}>Personal Health, Recovery &amp; Longevity Intelligence</h1>
          <p style={S.sub}>
            CareLogix ORACLE applies the same multi-agent intelligence used for
            markets, capital, grants, and contracts — now to your sleep,
            recovery, hydration, medication adherence, symptoms, and autoimmune
            wellness. <strong>Decision-support only. No diagnosis. No treatment.</strong>
          </p>
        </div>
        <div>
          {getAdminToken() && (
            <Link to="/adminone/executive-intelligence" style={S.link} data-testid="link-execintel">← Executive Intelligence</Link>
          )}
          <Link to="/intelligence-globe"              style={S.link} data-testid="link-globe">Intelligence Globe →</Link>
        </div>
      </header>

      {/* User selector */}
      <section style={S.section} data-testid="user-selector">
        <h2 style={S.sectionTitle}>Your LifeCommand session</h2>
        <p style={S.sectionSub}>
          Provide a stable identifier (your member id, email handle, etc.) so
          ORACLE can persist your trends. No PHI is required; data is
          decision-support telemetry.
        </p>
        <div style={S.inputRow}>
          <input data-testid="user-id-input" style={{ ...S.input, flex: 1, minWidth: 240 }} placeholder="Enter your member email or ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
          <button data-testid="user-load-btn" type="button" style={S.btn} onClick={onSelectUser}>Continue My LifeCommand</button>
        </div>
      </section>

      {/* KPIs */}
      <section data-testid="lifescore-kpis">
        <div style={S.grid}>
          <KPI testid="kpi-lifescore" label="LifeScore™" value={lifescoreValue != null ? lifescoreValue : "—"} sub={<Pill color={lifescoreColor}>{lifescoreBand}</Pill>} />
          <KPI testid="kpi-history"   label="Days Logged"           value={history.length} sub={history.length === 0 ? "NO_DATA_AVAILABLE" : "last 14d window"} />
          <KPI testid="kpi-wearables" label="Wearable Connections"  value={liveWearables.length}        sub={`/ ${wearables.length} catalogued`} />
          <KPI testid="kpi-conditions" label="Autoimmune Catalog"   value={conditions.length}           sub="conditions tracked" />
        </div>
      </section>

      {/* Quick actions */}
      <section style={S.section} data-testid="quick-actions">
        <h2 style={S.sectionTitle}>Quick Actions</h2>
        <p style={S.sectionSub}>One-tap entry points to the LifeCommand engines.</p>
        <div style={S.quickActions}>
          {quickActions.map((a) => (
            <button
              key={a.key}
              type="button"
              style={S.quickBtn}
              data-testid={`quick-${a.key}`}
              onClick={() => {
                if (a.key === "recovery_check")    runRecovery();
                else if (a.key === "flare_risk_check") runFlare(null);
                else if (a.key === "todays_score") loadUser(userId);
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </section>

      {/* Metric ingestion */}
      <section style={S.section} data-testid="metric-form">
        <h2 style={S.sectionTitle}>Log today’s signals</h2>
        <p style={S.sectionSub}>Every field is optional. LifeScore™ contributes only over the pillars you log; missing pillars are honored as NO_DATA.</p>
        <div style={S.inputRow}>
          {Object.entries({
            sleep_hours:               "Sleep (hours)",
            activity_minutes:          "Activity (min)",
            hydration_oz:              "Hydration (oz)",
            medication_adherence_pct:  "Medication adherence (%)",
            recovery_index:            "Recovery index (0-100)",
            symptom_score:             "Symptom score (0-100)",
            hrv:                       "HRV (ms)",
            resting_heart_rate:        "Resting HR (bpm)",
            pain_score:                "Pain score (0-10)",
          }).map(([k, label]) => (
            <div key={k} style={S.inputBox}>
              <span style={S.inputLabel}>{label}</span>
              <input
                type="number"
                style={S.input}
                value={form[k]}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                data-testid={`metric-${k}`}
              />
            </div>
          ))}
        </div>
        <div style={{ ...S.inputRow, marginTop: 14 }}>
          <button data-testid="submit-metrics-btn" type="button" style={S.btn} disabled={busy === "submit" || !userId} onClick={submitMetrics}>
            {busy === "submit" ? "Submitting…" : "Submit signals"}
          </button>
          <button data-testid="refresh-btn" type="button" style={S.btnSecondary} onClick={() => loadUser(userId)}>Refresh</button>
        </div>
      </section>

      {/* Recovery */}
      <section style={S.section} data-testid="recovery-section">
        <h2 style={S.sectionTitle}>Recovery Intelligence</h2>
        <p style={S.sectionSub}>Trend-based recovery deficit detection over your last 14 days.</p>
        <button data-testid="run-recovery-btn" type="button" style={S.btn} disabled={busy === "recovery" || !userId} onClick={runRecovery}>
          {busy === "recovery" ? "Analyzing…" : "Run Recovery Check"}
        </button>
        {recoveryBrief && (
          <div style={{ marginTop: 14 }} data-testid="recovery-brief">
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Pill color={RISK_COLOR[recoveryBrief.risk] || "#94a3b8"}>{recoveryBrief.risk}</Pill>
              <span style={{ color: "#94a3b8", fontSize: 12 }}>confidence: {recoveryBrief.confidence}</span>
              <Pill color={recoveryBrief.swarm_mode === "live" ? "#7AC860" : "#fcd34d"}>{(recoveryBrief.swarm_mode || "skip").toUpperCase()}</Pill>
            </div>
            {recoveryBrief.reasons && recoveryBrief.reasons.length > 0 && (
              <>
                <div style={{ ...S.kpiLabel, marginTop: 14 }}>Signals</div>
                <ul style={S.list}>{recoveryBrief.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>
            )}
            <div style={{ ...S.kpiLabel, marginTop: 14 }}>Recommendations</div>
            <ul style={S.list}>{(recoveryBrief.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
            {recoveryBrief.narrative && (
              <>
                <div style={{ ...S.kpiLabel, marginTop: 14 }}>LOGIX-2 LifeCoach narrative</div>
                <pre style={{ whiteSpace: "pre-wrap", color: "#cbd5e1", fontSize: 12.5, lineHeight: 1.55, margin: "6px 0 0" }}>{recoveryBrief.narrative}</pre>
              </>
            )}
          </div>
        )}
      </section>

      {/* Flare */}
      <section style={S.section} data-testid="flare-section">
        <h2 style={S.sectionTitle}>Autoimmune Flare Intelligence</h2>
        <p style={S.sectionSub}>Pattern-based flare-risk view across the catalogued conditions.</p>
        <div style={S.quickActions}>
          {conditions.map((c) => (
            <button key={c} type="button" style={S.quickBtn} data-testid={`flare-${c.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => runFlare(c)} disabled={busy === "flare" || !userId}>
              {c}
            </button>
          ))}
        </div>
        {flareBrief && (
          <div style={{ marginTop: 14 }} data-testid="flare-brief">
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Pill color={RISK_COLOR[flareBrief.risk] || "#94a3b8"}>{flareBrief.risk}</Pill>
              <span style={{ color: "#94a3b8", fontSize: 12 }}>{flareBrief.condition || "(no condition)"}</span>
            </div>
            <ul style={S.list}>{(flareBrief.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        )}
      </section>

      {/* Exercise animation library */}
      <section style={S.section} data-testid="exercise-library">
        <h2 style={S.sectionTitle}>LifeCoach Exercise Library</h2>
        <p style={S.sectionSub}>Accessibility-first animations with multi-language support stub. Tap one to preview the coach card.</p>
        <div style={S.quickActions}>
          {animations.map((a) => (
            <button key={a.key} type="button" style={S.quickBtn} data-testid={`anim-${a.key}`} onClick={() => setActiveAnim(a)}>
              {a.label} · {a.intensity}
            </button>
          ))}
        </div>
        {activeAnim && (
          <div style={{ marginTop: 14 }} data-testid="active-animation">
            <Pill color="#c5a059">{activeAnim.label}</Pill>
            <p style={{ color: "#cbd5e1", marginTop: 8, fontSize: 13 }}>
              Cartoon Coach demonstration available. Slow motion + voice guidance ready.
              Voice/animations themselves are part of the LOGIX-2 LifeCoach rendering layer.
            </p>
          </div>
        )}
      </section>

      {/* Wearables */}
      <section style={S.section} data-testid="wearables-section">
        <h2 style={S.sectionTitle}>Wearables</h2>
        <p style={S.sectionSub}>
          Connect Oura, Fitbit, Whoop, and Apple Health. Connectors marked
          <strong> Coming Soon</strong> will activate as integrations come online —
          ORACLE never displays fabricated samples.
        </p>
        <div style={S.quickActions}>
          {wearables.map((w) => (
            <Pill key={w.key} color={w.status === "LIVE" ? "#7AC860" : "#fcd34d"}>{w.name} · {w.status === "LIVE" ? "Live" : "Coming Soon"}</Pill>
          ))}
        </div>

        <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
          {["oura", "fitbit", "whoop", "apple_health"].map((key) => {
            const w = wearables.find((x) => x.key === key);
            const live = w && w.status === "LIVE";
            const friendly = {
              apple_health: "Apple Health integration is currently unavailable. We’ll notify members when it becomes available.",
              oura:         "Oura integration is currently unavailable. We’ll notify members when it becomes available.",
              fitbit:       "Fitbit integration is currently unavailable. We’ll notify members when it becomes available.",
              whoop:        "WHOOP integration is currently unavailable. We’ll notify members when it becomes available.",
            };
            return (
              <div key={key} style={{ ...S.kpi, padding: 14 }} data-testid={`wearable-card-${key}`}>
                <div style={S.kpiLabel}>{(w && w.name) || key}</div>
                <div style={{ marginTop: 6 }}>
                  <Pill color={live ? "#7AC860" : "#fcd34d"}>{live ? "Live" : "Coming Soon"}</Pill>
                </div>
                <p style={{ color: "#94a3b8", fontSize: 11.5, marginTop: 10, lineHeight: 1.5 }}>
                  {live ? "Connected — recent samples will appear in your LifeCommand." : friendly[key]}
                </p>
                <button
                  type="button"
                  data-testid={`wearable-sync-${key}`}
                  style={{ ...S.btnSecondary, marginTop: 8 }}
                  disabled={!userId || !live || busy === `sync:${key}`}
                  onClick={async () => {
                    setBusy(`sync:${key}`);
                    const out = await _post(`/api/oracle/lifecommand/wearables/${key}/sync`, { user_id: userId });
                    setBusy("");
                    if (out && out.ok && out.metrics) await loadUser(userId);
                    setRecoveryBrief(null);
                    if (out) window.alert(`${(w && w.name) || key}: ${out.status}${out.reason ? " — " + out.reason : ""}`);
                  }}
                >
                  {busy === `sync:${key}` ? "Syncing…" : "Sync now"}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <div style={S.compliance} data-testid="compliance-footer">{COMPLIANCE}</div>
    </main>
  );
}
